<!-- components/CircularFeatureDisplay.vue -->
<template>
    <div class="w-full relative max-w-screen-xl mx-auto overflow-hidden mb-20 md:-mt-80">
        <!-- Desktop Version - Hidden on mobile -->
        <div class="hidden lg:block">
            <NsJoinWaitlistForm class="absolute right-[10px] top-[460px]" />
            <div class="flex items-center justify-between">
                <!-- Centered circular track with rotating feature boxes -->
                <div class="relative">
                    <div class="relative -ml-[430px]" style="width: 800px; height: 800px;">
                        <!-- Outer track circle -->
                        <div
                            class="absolute inset-0 rounded-full bg-[conic-gradient(from_0deg,_#616CFF_0deg,_#616CFF_180deg,_transparent_180deg,_transparent_360deg)]">
                            <!-- Inner circle inside to show the background -->
                            <div class="w-full h-full rounded-full bg-[var(--background-primary)]"></div>
                        </div>

                        <!-- Inner circle -->
                        <div class="absolute top-1/2 left-1/2 border border-gray-800 rounded-full"
                            style="width: 400px; height: 400px; margin-top: -200px; margin-left: -200px;">
                        </div>

                        <!-- Center text with icon -->
                        <div
                            class="absolute top-1/2 right-36 transform -translate-x-1/2 -translate-y-1/2 flex items-center justify-end">
                            <div class="text-4xl mb-2">
                                <NsLogo name="NsVaahStore" class="w-8 h-8" />
                            </div>
                            <div class="text-lg font-bold text-[var(--text-secondary)] ml-2">Vaah Store</div>
                        </div>

                        <!-- Feature boxes positioned around the circle -->
                        <div v-for="(feature, index) in features" :key="feature.id"
                            class="absolute transition-all duration-1000 ease-in-out flex flex-col items-center"
                            style="width: 100px; height: 140px;" :style="getFeatureBoxPosition(index)">
                            <!-- Icon box -->
                            <div :class="['transition-all duration-1000 ease-in-out', currentFeature === index ? 'z-20 scale-110' : 'z-10']"
                                :style="getFeatureBoxRotation(index)">
                                <div
                                    class="w-28 h-28 flex items-center justify-center text-white font-bold text-xl shadow-lg rounded-2xl transition-all duration-1000 ease-in-out mb-2 bg-[linear-gradient(136.86deg,#25354C_23.56%,#07101F_99.16%)]">
                                    <component :is="feature.component" class="w-12 h-12" />
                                </div>
                                <p class="text-center">{{ feature.title }}</p>
                            </div>
                        </div>

                        <!-- Center indicator dot -->
                        <div class="absolute w-2 h-2 bg-red-500 rounded-full"
                            style="top: 8px; left: 50%; margin-left: -4px;"></div>
                    </div>
                </div>

                <!-- Main feature card positioned to the right of the circle -->
                <div class="relative w-96 h-80 overflow-hidden">
                    <!-- Card container with proper visibility -->
                    <div v-for="(feature, index) in features" :key="`desc-${feature.id}`"
                        class="absolute inset-0 transition-all duration-1000 ease-in-out flex items-center justify-center"
                        :style="getMainCardPosition(index)">
                        <component :is="getFeatureComponent(feature.type)" :feature="feature" />
                    </div>
                </div>
            </div>
        </div>

        <!-- Mobile Version - Only show join waitlist form -->
        <div class="block lg:hidden">
            <section class="relative w-full flex flex-col items-center justify-center text-white px-4 z-0 min-h-screen">
                <!-- Header -->
                <div class="z-20">
                    <div class="text-center mb-6">
                        <h2 class="text-2xl sm:text-3xl font-bold">Be the First to Experience Excellence</h2>
                        <p class="text-gray-400 mt-2">Join now for early access!</p>
                    </div>

                    <!-- Email input + button inside -->
                    <div class="relative w-full max-w-sm mx-auto mb-4">
                        <input v-model="email" placeholder="Email" type="email"
                            class="w-full rounded-full border-none p-3 pr-32 bg-[var(--background-input)] text-black outline-none focus:ring-2 focus:ring-blue-500" />
                        <button
                            class="absolute right-1 top-1 bottom-1 rounded-full px-4 text-white font-medium transition-all duration-200 hover:scale-105"
                            :style="{ background: 'linear-gradient(to right, #4ade80, #60a5fa)' }"
                            @click="joinWaitlist">
                            Join Waitlist
                        </button>
                    </div>

                    <p class="text-gray-400 text-sm mt-2 flex items-center gap-1 justify-center">
                        <i class="pi pi-lock"></i> We respect your privacy – No spam, ever!
                    </p>
                </div>

                <!-- Logo / Background Effect -->
                <!-- <div class="relative mt-16 flex items-center justify-center w-full">
                    <div
                        class="absolute w-[150vw] h-[150vw] max-w-[800px] max-h-[800px] -translate-y-1/2 bg-[var(--background-primary)] rounded-full z-10">
                    </div>
                    <div
                        class="absolute w-[150vw] h-[150vw] max-w-[800px] max-h-[800px] translate-y-1/2 bg-[var(--background-primary)] rounded-full z-10">
                    </div>
                    <div
                        class="absolute w-64 h-64 sm:w-80 sm:h-80 bg-green-400 rounded-full blur-3xl opacity-40 animate-pulse">
                    </div>
                    <div
                        class="absolute w-64 h-64 sm:w-80 sm:h-80 bg-blue-400 rounded-full blur-3xl opacity-40 animate-pulse delay-200">
                    </div>
                    <div
                        class="relative w-24 h-24 sm:w-32 sm:h-32 bg-gray-900 rounded-full flex items-center justify-center shadow-2xl z-10">
                        <img src="/logos/nuxtstore-logo.svg" alt="Nuxt.Store" class="w-12 h-12 sm:w-16 sm:h-16" />
                    </div>
                </div> -->

                <!-- Feature List for Mobile -->
                <div class="mt-8 w-full max-w-md mx-auto z-20">
                    <h3 class="text-xl font-bold text-center mb-6">Features</h3>
                    <div class="grid grid-cols-2 gap-4">
                        <div v-for="feature in features" :key="feature.id"
                            class="flex flex-col items-center p-4 rounded-lg bg-gray-800/50 backdrop-blur-sm border border-gray-700">
                            <component :is="feature.component" class="w-8 h-8 mb-2" />
                            <span class="text-sm font-medium text-center">{{ feature.title }}</span>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    </div>
</template>

<script setup>
const NsLogo = defineAsyncComponent(() => import('./NsLogo.vue'));
const NsJoinWaitlistForm = defineAsyncComponent(() => import('./NsJoinWaitlistForm.vue'))
const NsMultiVendorCard = defineAsyncComponent(() => import('./NsJoinWishlistCards.vue/NsMultiVendorCard.vue'))
const NsPreferredLanguageCard = defineAsyncComponent(() => import('./NsJoinWishlistCards.vue/NsPreferredLanguageCard.vue'))
const NsWishlistCard = defineAsyncComponent(() => import('./NsJoinWishlistCards.vue/NsWishlistCard.vue'))
const NsCouponsCard = defineAsyncComponent(() => import('./NsJoinWishlistCards.vue/NsCouponsCard.vue'))
const NsMultiCountryTax = defineAsyncComponent(() => import('./NsJoinWishlistCards.vue/NsMultiCountryTax.vue'))
const NsMultiCurrencyCard = defineAsyncComponent(() => import('./NsJoinWishlistCards.vue/NsMultiCurrencyCard.vue'))
const NsNotificationsCard = defineAsyncComponent(() => import('./NsJoinWishlistCards.vue/NsNotificationsCard.vue'))
const NsMultiStore = defineAsyncComponent(() => import('./NsJoinWishlistCards.vue/NsMultiStore.vue'))
const NsMultiStoreIcon = defineAsyncComponent(() => import('./atoms/icons/join-waitlist/NsMultiStoreIcon.vue'))
const NsMultiVendorIcon = defineAsyncComponent(() => import('./atoms/icons/join-waitlist/NsMultiVendorIcon.vue'))
const NsMultiCountryTaxIcon = defineAsyncComponent(() => import('./atoms/icons/join-waitlist/NsMultiCountryTaxIcon.vue'))
const NsMultiCurrencyIcon = defineAsyncComponent(() => import('./atoms/icons/join-waitlist/NsMultiCurrencyIcon.vue'))
const NsCouponsIcon = defineAsyncComponent(() => import('./atoms/icons/join-waitlist/NsCouponsIcon.vue'))
const NsWishlistIcon = defineAsyncComponent(() => import('./atoms/icons/join-waitlist/NsWishlistIcon.vue'))
const NsAbandonedCartIcon = defineAsyncComponent(() => import('./atoms/icons/join-waitlist/NsAbandonedCartIcon.vue'))
const NsMultiLingualIcon = defineAsyncComponent(() => import('./atoms/icons/join-waitlist/NsMultiLingualIcon.vue'))

const email = ref('')
const currentFeature = ref(0)
let rotationInterval = null

const features = [
    { id: 1, type: 'multistore', component: NsMultiStoreIcon, title: 'Multi Store', description: 'Lightning-fast performance with optimized code splitting, server-side rendering, and intelligent caching strategies that ensure your application loads in milliseconds.', tags: ['Speed', 'SSR', 'Optimization', 'Caching'], color: 'bg-blue-500' },
    { id: 2, type: 'multivendor', component: NsMultiVendorIcon, title: 'Multi Vendor', description: 'Beautiful, consistent design system with responsive components, dark mode support, and accessibility features built-in from the ground up.', tags: ['UI/UX', 'Responsive', 'Accessibility', 'Dark Mode'], color: 'bg-purple-500' },
    { id: 3, type: 'tax', component: NsMultiCountryTaxIcon, title: 'Multi Country Tax', description: 'Enterprise-grade security with built-in protection against XSS, CSRF, and other vulnerabilities. Includes authentication, authorization, and data encryption.', tags: ['Authentication', 'Encryption', 'Enterprise', 'Protection'], color: 'bg-green-500' },
    { id: 4, type: 'currency', component: NsMultiCurrencyIcon, title: 'Multi Currency', description: 'Horizontally scalable architecture that grows with your business. Supports microservices, auto-scaling, and handles millions of concurrent users.', tags: ['Microservices', 'Auto-scaling', 'Performance', 'Growth'], color: 'bg-yellow-500' },
    { id: 5, type: 'coupons', component: NsCouponsIcon, title: 'Coupons', description: 'Amazing developer experience with TypeScript support, hot reload, comprehensive testing tools, and detailed documentation for faster development.', tags: ['TypeScript', 'Testing', 'Documentation', 'Hot Reload'], color: 'bg-red-500' },
    { id: 6, type: 'wishlist', component: NsWishlistIcon, title: 'Wishlist', description: 'Mobile-first responsive design that works perfectly on all devices. Progressive Web App capabilities with offline support and native-like experience.', tags: ['PWA', 'Mobile', 'Offline', 'Responsive'], color: 'bg-indigo-500' },
    { id: 7, type: 'cart', component: NsAbandonedCartIcon, title: 'Abandoned Cart', description: 'Cutting-edge features and technologies that keep you ahead of the competition. AI-powered insights, machine learning capabilities, and future-ready architecture.', tags: ['AI', 'ML', 'Innovation', 'Future-ready'], color: 'bg-pink-500' },
    { id: 8, type: 'language', component: NsMultiLingualIcon, title: 'Multi Lingual', description: 'Support for multiple languages with RTL support, localization features, and dynamic language switching for global reach.', tags: ['i18n', 'RTL', 'Global', 'Localization'], color: 'bg-teal-500' }
]

const joinWaitlist = () => {
    if (!email.value) return alert('Please enter email')
    alert(`Joined waitlist with ${email.value}`)
    email.value = ''
}

const getFeatureComponent = (type) => {
    const componentMap = {
        multistore: NsMultiStore,
        multivendor: NsMultiVendorCard,
        tax: NsMultiCountryTax,
        currency: NsMultiCurrencyCard,
        coupons: NsCouponsCard,
        wishlist: NsWishlistCard,
        cart: NsNotificationsCard,
        language: NsPreferredLanguageCard
    }
    return componentMap[type] || NsMultiVendorCard
}

const getFeatureBoxPosition = (index) => {
    const totalFeatures = features.length
    const baseAngle = (360 / totalFeatures) * index
    const rotationOffset = (360 / totalFeatures) * currentFeature.value
    const angle = baseAngle - rotationOffset
    const radius = 300

    const radian = (angle * Math.PI) / 180
    const x = Math.cos(radian) * radius + 400 - 50
    const y = Math.sin(radian) * radius + 400 - 70

    return { left: `${x}px`, top: `${y}px` }
}

const getFeatureBoxRotation = (index) => {
    const totalFeatures = features.length
    const baseAngle = (360 / totalFeatures) * index
    const rotationOffset = (360 / totalFeatures) * currentFeature.value
    const angle = baseAngle - rotationOffset
    return { transform: `rotate(${angle}deg)` }
}
const getFeatureTitleRotation = (index) => {
    const totalFeatures = features.length
    const baseAngle = (360 / totalFeatures) * index
    const rotationOffset = (360 / totalFeatures) * currentFeature.value
    const angle = baseAngle - rotationOffset

    // Calculate the angle to keep text more horizontal but still inclined
    // Reduce the rotation angle to make text more readable
    const adjustedAngle = angle * 0.3 // Adjust this multiplier to control inclination (0 = horizontal, 1 = same as box)

    return { transform: `rotate(${adjustedAngle}deg)` }
}

const getMainCardPosition = (index) => {
    const totalFeatures = features.length
    const difference = (index - currentFeature.value + totalFeatures) % totalFeatures

    const radiusX = 250
    const radiusY = 350
    const centerX = 0
    const centerY = 0

    if (difference === 0) {
        return {
            transform: `translateX(${centerX}px) translateY(${centerY}px)`,
            opacity: '1',
            zIndex: '20',
            visibility: 'visible'
        }
    }
    else if (difference === 1) {
        const angle = -60
        const radian = (angle * Math.PI) / 180
        const x = Math.cos(radian) * radiusX + centerX
        const y = Math.sin(radian) * radiusY + centerY

        return {
            transform: `translateX(${x}px) translateY(${y}px) scale(0.8)`,
            opacity: '0.3',
            zIndex: '10',
            visibility: 'visible'
        }
    }
    else if (difference === totalFeatures - 1) {
        const angle = 60
        const radian = (angle * Math.PI) / 180
        const x = Math.cos(radian) * radiusX + centerX
        const y = Math.sin(radian) * radiusY + centerY

        return {
            transform: `translateX(${x}px) translateY(${y}px) scale(0.8)`,
            opacity: '0.3',
            zIndex: '10',
            visibility: 'visible'
        }
    }
    else if (difference === 2) {
        const angle = -90
        const radian = (angle * Math.PI) / 180
        const x = Math.cos(radian) * radiusX + centerX
        const y = Math.sin(radian) * radiusY + centerY

        return {
            transform: `translateX(${x}px) translateY(${y}px) scale(0.6)`,
            opacity: '0.1',
            zIndex: '5',
            visibility: 'visible'
        }
    }
    else if (difference === totalFeatures - 2) {
        const angle = 90
        const radian = (angle * Math.PI) / 180
        const x = Math.cos(radian) * radiusX + centerX
        const y = Math.sin(radian) * radiusY + centerY

        return {
            transform: `translateX(${x}px) translateY(${y}px) scale(0.6)`,
            opacity: '0.1',
            zIndex: '5',
            visibility: 'visible'
        }
    }
    else {
        return {
            transform: `translateX(500px) translateY(500px) scale(0.5)`,
            opacity: '0',
            zIndex: '0',
            visibility: 'hidden'
        }
    }
}

const startAutoRotation = () => {
    rotationInterval = setInterval(() => {
        currentFeature.value = (currentFeature.value + 1) % features.length
    }, 4000)
}

onMounted(() => {
    startAutoRotation()
})

onUnmounted(() => {
    if (rotationInterval) clearInterval(rotationInterval)
})
</script>